import React from "react";
import { withRouter } from "react-router-dom";
import { flowRight as compose } from "lodash";
import { withStore } from "../../store";
import { SVGImage } from "../../assets/images";
import Slider from "react-slick";
import Slide from "../Slide/Slide";
import SubscribersOptions from "../SubscribersOptions/SubscribersOptions";
import "./Hero.scss";
// MET2-2953 (full fill): HERO_SLIDE_VH and DESKTOP_MARGIN no longer drive the
// slide height -- it now fills the space below #app-header exactly. Both
// constants are still used elsewhere (Login.tsx, Slide.scss's mobile/tablet
// overrides), just not here anymore.
import {
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE,
} from "../../utils/constants";

// MET2-2953: how recently focus must have moved for a scroll event to count as
// "the browser scrolled to reveal the newly-focused element" rather than as the
// user scrolling. Tab -> focusin -> the browser's focus-into-view scroll all
// happen within a frame or two; a wheel gesture seconds after a click does not.
// 150ms is comfortably above the frame budget and far below human re-engagement
// time. Measured: the MET2-2892 Tab-past-the-dots case still suppresses, and the
// wheel-back-to-top case (dot click, then scroll up 2s later) now rotates.
// Context: docs/components/hero-slide-ownership.md
const FOCUS_DRIVEN_SCROLL_WINDOW_MS = 150;

class Hero extends React.Component<any, any> {
  slider;
  slideHeight;
  appHeaderObserver;
  appHeaderHeight;

  constructor(props) {
    super(props);
    this.state = {
      slideIndex: 0,
      infoIndex: 0,
      slidesCount: props.slides.length,
    };
  }

  componentDidMount() {
    document.dispatchEvent(new CustomEvent("metopera:hero-active", { detail: { active: true } }));
    window.addEventListener("resize", this.onResize);
    this.onResize();
    window.scrollTo({ top: 0 });

    const appHeader = document.querySelector("#app-header");
    this.appHeaderHeight = appHeader.offsetHeight;
    // MET2-2866: expose the header height so the pinned "Hi, <name>" account
    // dropdown (.Hero-header, position:fixed) sits just below the real header.
    document.documentElement.style.setProperty("--app-header-height", `${appHeader.offsetHeight}px`);
    this.appHeaderObserver = new ResizeObserver(
      function () {
        if (appHeader.offsetHeight !== this.appHeaderHeight) {
          window.scrollTo({ top: window.scrollY });
          this.appHeaderHeight = appHeader.offsetHeight;
        }
        document.documentElement.style.setProperty("--app-header-height", `${appHeader.offsetHeight}px`);
      }.bind(this)
    );
    this.appHeaderObserver.observe(appHeader);

    // MET2-2892 follow-up: when keyboard nav brings focus back into the
    // hero from below, the browser's auto-scroll only moves the page
    // enough to put the focused element in the viewport — not enough to
    // clear minScrollHeight, so .Hero.scrolled stays applied. Result:
    // hero is opacity-faded, dots stay hidden, slide content is absolute-
    // positioned; user sees an "effectively gone" hero with one slide CTA
    // visible behind an overlay. Restore the hero in full when any
    // descendant gets focus while we're in the scrolled state.
    //
    // Also track focus-in/focus-out to suppress the scroll-driven slide
    // rotation in onScroll when Tab causes auto-scroll. See onScroll for
    // the gate.
    this.lastFocusInHero = false;
    this.justLeftHero = false;
    document.addEventListener("focusin", this.onHeroFocusIn);

    // MET2-2953: Slide.tsx no longer renders `info-active` itself (see
    // heroManaged), so the first slide's overlay would stay invisible until
    // the first scroll or setState. Apply it now, synchronously after mount.
    this.applyInfoActive();
    // ⚠️ ...and again on the next task. react-slick's first render happens
    // before it has evaluated its own breakpoints, so on desktop it renders
    // the SLICK structure, then setStates in its componentDidMount and
    // re-renders as `unslick`. That swaps the wrapper chain, which remounts
    // every Slide onto fresh DOM nodes — dropping the class we just applied.
    // (`inert` survives it only because Slide.componentDidMount re-syncs it.)
    // Hero's own componentDidUpdate does not fire for react-slick's internal
    // setState, so without this the hero shows NO overlay text until the user
    // scrolls far enough to change infoIndex.
    // Context: docs/components/hero-slide-ownership.md
    setTimeout(this.applyInfoActive, 0);
  }

  lastFocusInHero: boolean;
  justLeftHero: boolean;
  justLeftHeroTimer: any;
  // MET2-2953: timestamp of the last focus move anywhere on the page. Lets
  // onScroll tell a focus-into-view auto-scroll (which must not rotate the
  // carousel) from a real user scroll that merely happens to occur while
  // focus is parked inside the hero (which must).
  lastFocusMoveAt: number = 0;

  onHeroFocusIn = (e: FocusEvent) => {
    this.lastFocusMoveAt = Date.now();
    const target = e.target as HTMLElement | null;
    const hero = document.querySelector("div.Hero");
    const inHero = !!(target && hero && hero.contains(target));

    // MET2-2891 mobile safety net: if focus-into-view nudged the slick list
    // sideways, snap it back so the carousel can't sit between slides.
    if (inHero) this.resetSlickListScroll();

    // Re-entering a scrolled-past hero: jump to the active slide's natural
    // position (same formula onClick uses) so background image + overlay
    // info both match state.slideIndex, then run onScroll to align layout
    // classes (active/inactive + margin-tops). Use behavior:"auto", not
    // smooth — a smooth scroll fights the browser's focus-into-view
    // auto-scroll and lands between two slide snap points (visible as a
    // half-and-half stacked image, slide N-1 on top of slide N).
    if (target && hero && hero.contains(target) && hero.classList.contains("scrolled")) {
      const appHeaderEl = document.querySelector("#app-header") as HTMLElement | null;
      const headerOffset = appHeaderEl ? appHeaderEl.offsetHeight : 0;
      const targetY = this.state.slideIndex * this.slideHeight + headerOffset;
      window.scrollTo({ top: targetY, behavior: "auto" });
      this.onScroll();

      // Shift+Tab re-entry: the dots are visibility:hidden while the hero
      // is .scrolled (so back-tab from below was landing on whatever
      // overlay CTA happened to be the last focusable element of the
      // active slide — Login, Start Free Trial, etc.). After onScroll
      // above removes .scrolled and makes the dots focusable again,
      // redirect focus to the active dot so re-entry lands on the
      // recognizable hero-navigation control, not a buried slide CTA.
      const activeDot = hero.querySelector(
        `.hero-dot[data-index="${this.state.slideIndex}"]`
      ) as HTMLElement | null;
      if (activeDot && activeDot !== target) {
        activeDot.focus();
      }
    }

    // Track Tab transitions across the hero boundary. When focus *just left*
    // the hero (was inside, now outside), set a short-lived flag so the
    // next scroll event — almost always the browser's focus-into-view
    // auto-scroll — doesn't rotate the carousel to the slide that
    // happens to align with that scroll position.
    if (this.lastFocusInHero && !inHero) {
      this.justLeftHero = true;
      clearTimeout(this.justLeftHeroTimer);
      this.justLeftHeroTimer = setTimeout(() => { this.justLeftHero = false; }, 600);
    }
    this.lastFocusInHero = inHero;
  };

  // MET2-2953: Hero is the SINGLE WRITER of the mutable per-slide layout —
  // `active` / `inactive` / `info-active` and the inline margin-top. Slide.tsx
  // renders a constant className (`heroManaged`) so React can never rewrite
  // the class attribute over these writes. componentDidUpdate re-applies them
  // after every React render, because a render triggered by setState (rather
  // than by a scroll event) would otherwise leave the DOM describing the
  // PREVIOUS slideIndex until the user happens to scroll again — and at a
  // slide boundary the user has usually just stopped.
  // Context: docs/components/hero-slide-ownership.md
  componentDidUpdate() {
    this.applySlideLayout();
  }

  // Which slide's overlay the user can see. Keyed to infoIndex (flips at the
  // halfway point), NOT slideIndex (flips at the slide boundary). Runs on every
  // viewport, since react-slick drives infoIndex via onSlickAfterChange on
  // mobile where onScroll is not attached at all.
  // ⚠️ MET2-2953: this also applies the box pinning, so the two overlay-state
  // writes can never drift apart. They need the SAME apply-points for the same
  // reason -- react-slick's unslick re-render remounts every Slide onto fresh
  // DOM nodes and drops both classes -- and pinning applied at only some of
  // them is worse than not pinning at all: it then lands on whichever event
  // happens to fire first, so the jump merely MOVES. Measured on the local
  // build when applyBoxPinning hung off componentDidUpdate alone: the box was
  // unpinned until the first setState (infoIndex flipping at slide 0's halfway
  // point) and then snapped 405px at scrollY 450.
  // Context: docs/components/hero-slide-ownership.md
  applyInfoActive = () => {
    this.applyBoxPinning();
    const { infoIndex } = this.state;
    document.querySelectorAll(".Hero-slider .Slide").forEach((el, i) => {
      el.classList.toggle("info-active", i === infoIndex);
    });
  };

  // The desktop scroll-stack positioning: slides at or before slideIndex are
  // `active` (position:absolute, offset down the page by their own height);
  // slides after it are `inactive` (position:fixed, pinned under the header).
  // Extracted verbatim from onScroll so componentDidUpdate can re-run it.
  applySlideStack = (headerOffset) => {
    document.querySelectorAll(".Hero-slider .Slide").forEach(
      function (el, i) {
        if (i > this.state.slideIndex) {
          el.classList.remove("active");
          el.classList.add("inactive");
          // 🔴 MET2-2953 (fourth mechanism). These are the not-yet-reached
          // slides, `position: fixed`, revealed underneath as the active slide
          // scrolls up. A `-headerOffset` margin used to pull them to viewport
          // top: 0, which puts their top `headerOffset` px BEHIND the sticky
          // #app-header and leaves their bottom exactly `headerOffset` px short
          // of the viewport bottom. Measured on QA at 2000x1250 (header 100,
          // slideHeight 1150): inactive slides rendered 0..1150 against a
          // 1250px viewport -- a 100px white band, on every slide after the
          // first. It also made the viewport-fixed CTA box look like it jumped
          // up, because the image bottom moved 100px while the box did not.
          //
          // 0 puts them at `headerOffset..vh` instead: flush to the bottom, and
          // in the SAME place the slide occupies once it flips to `active`
          // (whose margin is `i * slideHeight + headerOffset`), so the
          // fixed -> absolute handoff is seamless rather than a 100px jump.
          // Mirror of the active branch below -- fixing one without the other
          // is what let this survive the previous pass.
          // Context: docs/components/hero-slide-ownership.md
          el.style.marginTop = "0";
        } else {
          el.classList.add("active");
          el.classList.remove("inactive");
          // MET2-2953 (third mechanism, generalized for full-fill). #app-header
          // is `position: sticky`, so it re-pins across the top `headerOffset`
          // px of the VIEWPORT at every scroll position, not just once at the
          // top of the document. Slide 0 never overlaps it (it starts right
          // after the header in normal flow); every later slide needs that same
          // headerOffset added ONCE (not multiplied by i -- the header re-pins
          // the same way regardless of index) so its stacked position lands
          // where it visually should, not partly behind the header.
          // this.slideHeight is now `vh - headerOffset` (full-fill: the slide
          // fills exactly the space below the header, no residual margin), so
          // this is a plain px stack: i whole slides, plus the one header
          // compensation. Context: docs/components/hero-slide-ownership.md.
          //
          // ⚠️ Slide 0 keeps `marginTop: 0` and that is CORRECT -- do not
          // "unify" the branches. Tried and reverted 2026-09-15: an `active`
          // slide is `position: absolute`, but its containing block is
          // `.Hero-slider`, which is itself in normal flow BELOW the sticky
          // header, so slide 0's absolute origin already sits at
          // `headerOffset`. Adding the compensation pushed it a full header
          // below the viewport bottom at rest -- measured 1279x842, check F's
          // slide-0 control went 0px to **-76px**.
          //
          // The asymmetry is real: each slide must be flush at ITS OWN resting
          // scroll position, and slide 0 rests at `scrollY = 0` where no sticky
          // re-pin has happened yet, while slide i>0 rests at
          // `i * slideHeight + headerOffset` where it has. Same reason
          // mechanism C added one flat headerOffset rather than `i *`.
          //
          // ⚠️ What made this look broken: measuring only the `info-active`
          // slide's own rect shows slide 0 ending `headerOffset` short of the
          // viewport once you scroll past `headerOffset`. That is not a band --
          // the not-yet-reached slides are `position: fixed` at
          // `headerOffset..vh` directly behind it and fill exactly that space
          // (mechanism D). Judge fill by the MAX bottom across all slides, the
          // way verify-hero-no-jump does, never by the active slide alone.
          el.style.marginTop = i > 0 ? `${i * this.slideHeight + headerOffset}px` : "0";
        }
      }.bind(this)
    );
  };

  // Clear the desktop scroll-stack back to the CSS default. Used above the
  // header offset and on viewports where the stack does not apply.
  clearSlideStack = () => {
    document.querySelectorAll(".Hero-slider .Slide").forEach(function (el: HTMLElement) {
      el.classList.remove("active");
      el.classList.remove("inactive");
      el.style.removeProperty("margin-top");
    });
  };

  // MET2-2953 (fifth mechanism, the reported "jump"). The overlay box is
  // `position: absolute; bottom: 0` by default and `position: fixed` with a
  // 45px bottom margin once `.active` is on it (SlideInfo.scss). Those are two
  // different motions -- riding with the slide vs pinned to the viewport -- and
  // `.active` used to be added only once scrollY reached headerOffset, so the
  // box rode up with slide 0 for the first ~76px and then SNAPPED back down to
  // its pinned offset. Measured on QA at 1279x842: box top 500 -> 525 across
  // scrollY 70 -> 80, i.e. it moved 25px DOWN during a 10px DOWNWARD scroll.
  // That is the reporter's "the jump in the text box happens in the first
  // carousel item".
  //
  // There is no scrollY at which pinning can begin without a jump: below the
  // threshold the box rides with the page, above it the box is stationary, so
  // any boundary between the two regimes is a discontinuity. The fix is to
  // delete the regime, not move it -- the box is pinned for the WHOLE hero,
  // from scrollY 0. Desktop only, matching where onScroll is attached at all.
  //
  // The box carries no bottom offset in any state (SlideInfo.scss), so pinning
  // it from scrollY 0 leaves the resting position exactly where it was: flush
  // with the slide's bottom edge, which is what the design specifies.
  // Context: docs/components/hero-slide-ownership.md
  // ⚠️ The navigation dots are pinned on the SAME apply-point, not separately.
  // `.Hero-navigation-dots.active` is the same fixed/absolute split keyed to the
  // same class name, and Hero.scss requires the dots to ride exactly 50px above
  // the box's lower edge (`calc($desktop-margin * 1.5 + 50px)` against the box's
  // `$desktop-margin * 1.5`). Pinning the box while leaving the dots on the old
  // scroll threshold breaks that pairing at rest -- measured 1279x842: box
  // bottom 797 with dots bottom 792, i.e. 5px apart instead of 50 -- and the
  // dots then jump 45px by themselves when the threshold is crossed. Whatever
  // this method does to one it must do to the other.
  applyBoxPinning = () => {
    const pinned = window.innerWidth > BREAKPOINT_TABLET;
    document
      .querySelectorAll(".Hero-slider .Slide .slide-overlay-info-wrapper")
      .forEach(function (el) {
        el.classList.toggle("active", pinned);
      });
    const dots = document.querySelector(".Hero-navigation-dots");
    if (dots) dots.classList.toggle("active", pinned);
  };

  applySlideLayout = () => {
    // applyInfoActive also applies the box pinning -- see the note on it.
    this.applyInfoActive();

    const appHeader = document.querySelector("#app-header") as HTMLElement | null;
    if (!appHeader) return;
    // 🔴 MET2-2953 (sixth mechanism): this used to also require
    // `window.scrollY >= appHeader.offsetHeight`, which left the stack CLEARED
    // for the first headerOffset px of scroll. Cleared means slide 0 is back in
    // normal flow and the later slides are not `position: fixed` behind it, so
    // nothing fills the space slide 0 vacates as it scrolls up: page background
    // shows through, growing 1px per px scrolled. Measured on deployed QA at
    // 2000x1250 with real wheel events: gap 40px at scrollY 40, 90px at 90,
    // closing the instant the stack engaged. That is the reporter's "that white
    // box still appears on the first slide once you start a gradual scroll
    // down", and it is the LAST of this ticket's `scrollY >= headerOffset`
    // thresholds -- same shape as the box-pinning one above.
    //
    // ⚠️ It survived six passes because every check sampled too coarsely to see
    // it: verify-hero-no-white-band takes 13 screenshots across the whole hero,
    // ~450px apart at this viewport, so a band that only exists below y=100
    // falls between samples 0 and 1. verify-hero-no-jump now asserts hero fill
    // at every 10px wheel step for exactly this reason.
    // The stack is desktop-only, matching where onScroll is attached at all.
    if (window.innerWidth > BREAKPOINT_TABLET) {
      this.applySlideStack(appHeader.offsetHeight);
    } else {
      this.clearSlideStack();
    }
  };

  componentWillUnmount() {
    document.dispatchEvent(new CustomEvent("metopera:hero-active", { detail: { active: false } }));
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("scroll", this.onTabletScroll);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("focusin", this.onHeroFocusIn);
    this.appHeaderObserver && this.appHeaderObserver.unobserve(document.querySelector("#app-header"));
  }

  // MET2-2891 mobile: on viewports below BREAKPOINT_TABLET, react-slick is
  // active (infinite carousel). Every slide other than the current one —
  // both the .slick-cloned duplicates AND the real off-screen slides — keeps
  // its inner CTAs (Start Free Trial / Login / Give-Redeem / Watch Now) in
  // the Tab order. slick only sets aria-hidden + tabindex=-1 on the slide
  // wrapper, not the inner focusables.
  //
  // The damage is two-fold when you Tab into an off-screen slide:
  //   1. focus lands on a control the user can't see (the original
  //      "tab cycles through free trial / login / slides" report), and
  //   2. the browser scrolls the overflow:hidden .slick-list sideways to
  //      bring that focused control into view, which leaves the carousel
  //      parked between two slides — the "stuck mid-transition / split
  //      image" the user sees.
  //
  // Fix: make EVERY non-current slick slide inert (clones + real off-screen
  // slides), so Tab can only ever reach the single visible slide; and reset
  // any horizontal scroll the list may have already taken. Re-run on
  // init / re-init / afterChange so it tracks slick's current slide as the
  // user navigates by dot or swipe.
  syncSlickInert = () => {
    // MET2-2953: covers the tablet/mobile slick path, where onScroll is never
    // attached, so this (onInit / onReInit / afterChange) is the only hook that
    // re-asserts the visible-overlay class after slick rebuilds its DOM.
    this.applyInfoActive();

    const root = document.querySelector(".Hero-slider");
    if (!root || !root.classList.contains("slick-initialized")) {
      // Desktop (unslick): no slick wrappers to mark inert. The applyInfoActive
      // above has already run, and Slide.tsx's own isInfoActive-driven syncInert
      // handles per-slide inertness on this path.
      return;
    }
    root.querySelectorAll(".slick-slide").forEach((el) => {
      const isCurrent = el.classList.contains("slick-current");
      (el as any).inert = !isCurrent;
    });
    this.resetSlickListScroll();
  };

  // The slick list uses a track transform for positioning and should never
  // scroll horizontally. Focusing an off-screen element can still nudge its
  // scrollLeft; snap it back so the carousel can't sit between slides.
  resetSlickListScroll = () => {
    const list = document.querySelector(".Hero-slider .slick-list") as HTMLElement | null;
    if (list && list.scrollLeft !== 0) list.scrollLeft = 0;
  };

  // Keep React state (and the custom dots) aligned with the slide slick
  // actually shows after a dot/swipe change, then re-sync inert.
  onSlickAfterChange = (current) => {
    if (typeof current === "number" && current !== this.state.slideIndex) {
      this.setActiveNavigationDot(current);
      this.setState({ slideIndex: current, infoIndex: current });
    }
    this.syncSlickInert();
  };

  setActiveNavigationDot = (index) => {
    document.querySelectorAll(".Hero-navigation-dots .hero-dot").forEach((el) => {
      if (Number(el.getAttribute("data-index")) !== index) {
        el.classList.remove("active");
        el.setAttribute("aria-selected", "false");
      } else {
        el.classList.add("active");
        el.setAttribute("aria-selected", "true");
      }
    });
  };

  onResize = () => {
    const homePageContent = document.querySelector(".HomePage > .content"),
      hero = document.querySelector("div.Hero"),
      dots = hero.querySelector(".Hero-navigation-dots"),
      vh = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      );
    // MET2-2953 (full fill): the slide fills the space below #app-header
    // exactly (vh - headerOffset), not the old 95vh - 60px design margin.
    // #app-header is `position: sticky`, so it re-pins across the viewport's
    // top `headerOffset` px at EVERY scroll position, not just once at the
    // top of the document -- that's why the per-slide stacking below still
    // adds one flat headerOffset even though slideHeight already excludes it.
    // Context: docs/components/hero-slide-ownership.md
    const appHeaderForResize = document.querySelector("#app-header") as HTMLElement | null;
    const headerOffsetForResize = appHeaderForResize ? appHeaderForResize.offsetHeight : 0;
    this.slideHeight = vh - headerOffsetForResize;
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("scroll", this.onTabletScroll);

    if (homePageContent && (window.innerWidth > BREAKPOINT_TABLET)) {
      homePageContent.style.marginTop = `${
        headerOffsetForResize + (this.state.slidesCount - 1) * this.slideHeight
      }px`;
      dots.style.height = `${
        12 * this.state.slidesCount + 15 * (this.state.slidesCount - 1)
      }px`;
      window.addEventListener("scroll", this.onScroll);
      setTimeout(
        function () {
          if (window.innerWidth > BREAKPOINT_TABLET) {
            this.onScroll();
            const scrollY = window.scrollY;
            if (scrollY > 0) {
              window.scrollTo({ top: scrollY - 1 });
            }
          }
        }.bind(this),
        50
      );
    } else {
      if (homePageContent && hero) {
      homePageContent.style.removeProperty("margin-top");
      hero.style.removeProperty("opacity");
      hero.classList.remove("scrolled");
      hero.classList.remove("fully-scrolled");
      document.querySelectorAll(".Hero-slider .Slide").forEach(function (el) {
        el.style.removeProperty("margin-top");
      });
      dots.style.removeProperty("height");

      if (window.innerWidth > BREAKPOINT_MOBILE) {
        window.addEventListener("scroll", this.onTabletScroll);
        setTimeout(
          function () {
            if (
              window.innerWidth > BREAKPOINT_MOBILE &&
              window.innerWidth <= BREAKPOINT_TABLET
            ) {
              this.onTabletScroll();
            }
          }.bind(this),
          50
        );
      }
    }
  }
  };

  onScroll = () => {
    // MET2-2953: cheap, idempotent re-assertion of the whole mutable layout on
    // every scroll frame -- visible-overlay class, box/dots pinning AND the
    // slide stack -- so nothing that re-renders the slider can leave the DOM
    // disagreeing with state.
    //
    // ⚠️ This calls applySlideLayout, not applyInfoActive. onScroll used to
    // apply the stack itself, inside its own `scrollY >= headerOffset` branch
    // below, which meant the stack was never applied at all while scrolling
    // through the first headerOffset px -- applySlideLayout only ran from
    // componentDidUpdate, and no setState happens in that range because the
    // slide index is already 0. Removing the gate from applySlideLayout alone
    // therefore changed nothing measurable; the gate had to stop existing on
    // BOTH paths. One owner, called unconditionally.
    this.applySlideLayout();

    const scrollY = window.scrollY,
      headerOffset = document.querySelector("#app-header").offsetHeight;

    if (scrollY >= headerOffset) {
      const unparsedIndex = (scrollY + 1 - headerOffset) / this.slideHeight,
        decimals = unparsedIndex - Math.floor(unparsedIndex),
        newIndex =
          Math.trunc(unparsedIndex) > this.state.slidesCount - 1
            ? this.state.slidesCount - 1
            : Math.trunc(unparsedIndex),
        infoIndex =
          newIndex === this.state.slidesCount - 1
            ? newIndex
            : decimals > 0.5
            ? Math.ceil(unparsedIndex)
            : Math.floor(unparsedIndex),
        minScrollHeight =
          (this.state.slidesCount - 1) * this.slideHeight + 1 + headerOffset,
        hero = document.querySelector("div.Hero"),
        header = document.querySelector(".Header");

      // The stack is applied once at the top of onScroll now, on every frame
      // and at every scroll position. See the note there.
      // MET2-2953: neither the box's nor the dots' `active` (pinning) class is
      // set here any more. Adding them at this threshold and removing them in
      // the else branch below is what produced the first-slide jump;
      // applyBoxPinning now owns both for the whole hero, keyed to viewport
      // width only. See applyBoxPinning.

      // MET2-2892 followup: when keyboard focus is inside the hero, suppress
      // the scroll-driven active-slide change. The scroll-snap rotation
      // logic above is meant for users scrolling through the page with a
      // wheel or scrollbar; programmatic browser-auto-scroll triggered by
      // Tab → focus-into-view is NOT user intent to rotate. Without this
      // gate, Tab past dot 6 scrolled the page far enough that
      // activeSlide jumped 0 → 5 ("first slide to last slide automatically
      // when we tab past the indicators without selecting one"). Mouse-
      // driven scroll still rotates normally because at that point the
      // active element is the page body, not anything inside .Hero.
      const heroEl = document.querySelector("div.Hero");
      const focusInHero = !!(document.activeElement && heroEl && heroEl.contains(document.activeElement));
      // ⚠️ MET2-2953: the test is "did focus JUST move into the hero", not
      // "is focus somewhere in the hero". `focusInHero` alone suppressed every
      // scroll for as long as focus stayed parked on a hero control — so after
      // clicking a dot (which leaves focus on that dot), wheeling back up to the
      // top never rotated `infoIndex` back and slide 0's overlay stayed hidden
      // while its image was on screen. Requiring the focus move to be RECENT
      // keeps MET2-2892's real case (Tab -> focusin -> the browser's
      // focus-into-view scroll, all within a frame or two) suppressed while
      // letting a deliberate wheel gesture rotate normally.
      const focusDrivenScroll =
        focusInHero && Date.now() - this.lastFocusMoveAt < FOCUS_DRIVEN_SCROLL_WINDOW_MS;
      // Also suppress for the brief window right after Tab moves focus
      // OUT of the hero — that scroll is the browser's focus-into-view,
      // not user-intended carousel rotation. See onHeroFocusIn.
      // BUT explicit dot click should always rotate (user-initiated) —
      // userClickedDot escapes the suppression. See onClick.
      const suppressRotation = (focusDrivenScroll || this.justLeftHero) && !this.userClickedDot;

      if (!suppressRotation && this.state.slideIndex !== newIndex) {
        this.setActiveNavigationDot(newIndex);
        this.setState({
          slideIndex: newIndex,
        });
      }

      // Same gate as slideIndex above — don't rotate the visually-featured
      // slide based on focus-induced scroll. (`focusInHero` is defined just
      // above the slideIndex block.)
      // MET2-2953: only setState here. componentDidUpdate -> applyInfoActive
      // owns the class, so the DOM and state can no longer disagree.
      if (!suppressRotation && this.state.infoIndex !== infoIndex) {
        this.setState({
          infoIndex: infoIndex,
        });
      }

      let delta =
        (minScrollHeight + this.slideHeight - scrollY) / this.slideHeight;
      delta = delta > 1 ? 1 : delta < 0 ? 0 : delta;
      hero.style.opacity = Math.abs(delta);

      if (scrollY > minScrollHeight) {
        hero.classList.add("scrolled");
      } else {
        hero.classList.remove("scrolled");
      }

      if (scrollY > minScrollHeight + 200) {
        hero.classList.add("fully-scrolled");
        document.dispatchEvent(new CustomEvent("metopera:hero-active", { detail: { active: false } }));
      } else {
        hero.classList.remove("fully-scrolled");
        document.dispatchEvent(new CustomEvent("metopera:hero-active", { detail: { active: true } }));
      }
    } else {
      // Scrolled back above the header — fully restore hero to its initial state.
      const hero = document.querySelector("div.Hero") as HTMLElement;
      hero.style.removeProperty("opacity");
      hero.classList.remove("scrolled");
      hero.classList.remove("fully-scrolled");
      // MET2-2953: the stack is deliberately NOT cleared here any more. Clearing
      // it below the header offset is what let page background show under slide
      // 0 during the first pixels of a scroll. applySlideLayout owns the stack
      // now and keys it to viewport width only. See applySlideLayout.

      // MET2-2953: above the header offset the hero is at its resting position,
      // so slide 0 is unambiguously the one on screen — there is no rotation
      // ambiguity left to protect and no suppression rule should apply. Force
      // state back to 0 so scrolling to the top always restores slide 0's
      // overlay, whatever route got us here. Exempt an in-flight dot click,
      // whose smooth scroll passes through this range on its way down and
      // owns the target index itself (see onClick).
      if (!this.userClickedDot && (this.state.slideIndex !== 0 || this.state.infoIndex !== 0)) {
        this.setActiveNavigationDot(0);
        this.setState({ slideIndex: 0, infoIndex: 0 });
      }
      // MET2-2953: the box's `active` (pinning) class is deliberately NOT
      // removed here. Un-pinning it above the header offset and re-pinning it
      // below was the first-slide jump. See applyBoxPinning.
      document.dispatchEvent(new CustomEvent("metopera:hero-active", { detail: { active: true } }));
    }
  };

  onTabletScroll = () => {
    const scrollY = window.scrollY,
      headerOffset = document.querySelector("#app-header").offsetHeight;

    if (scrollY >= headerOffset) {
      // tablet scroll handling (subscriber options removed)
    }
  };

  userClickedDot: boolean = false;
  userClickedDotTimer: any;
  dotClickSettleTimer: any;

  onClick = (index) => {
    // MET2-2892 followup: dot click should rotate the slides even though
    // the click puts focus inside .Hero (which normally suppresses scroll-
    // driven rotation in onScroll). Set a brief bypass flag so this
    // user-initiated rotation goes through; clear after the smooth-scroll
    // settles. The flag is checked in onScroll's suppress condition.
    this.userClickedDot = true;
    clearTimeout(this.userClickedDotTimer);
    this.userClickedDotTimer = setTimeout(() => { this.userClickedDot = false; }, 1500);

    const headerOffset = document.querySelector("#app-header").offsetHeight,
      scrollPosition = index * this.slideHeight + headerOffset;
    window.scrollTo({ top: scrollPosition, behavior: "smooth" });
    this.onScroll();

    // MET2-2892 #7: clicking a dot FORWARD of the current selection lights
    // up the dot one behind the clicked one after the animation settles.
    // Smooth scroll undershoots the target by 1-2px on Chrome, which
    // makes Math.trunc((scrollY + 1 - headerOffset) / slideHeight) round
    // DOWN to (index - 1). Backward clicks don't hit this because the
    // truncation already lands on the target floor. Force-set the final
    // state after the scroll has settled.
    clearTimeout(this.dotClickSettleTimer);
    this.dotClickSettleTimer = setTimeout(() => {
      if (this.state.slideIndex !== index || this.state.infoIndex !== index) {
        this.setActiveNavigationDot(index);
        // MET2-2953: setState only — componentDidUpdate -> applySlideLayout
        // writes info-active and the stack classes from the new state.
        this.setState({ slideIndex: index, infoIndex: index });
      }
    }, 1000);
  };

  render() {
    const { slides, store: { user, userFetchPending } } = this.props;
    const settings = {
      dots: true,
      autoplay: false,
      infinite: true,
      arrows: false,
      mobileFirst: true,
      responsive: [
        {
          breakpoint: 9999,
          settings: "unslick",
        },
        {
          breakpoint: BREAKPOINT_TABLET,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    };
    return (
      <div className="Hero">
        {/* The .Hero-header slot is auth-state exclusive, as it was originally:
         * logged out gets the stacked "Met Opera on Demand" brand mark over the
         * slides; logged in gets the "Hi, <name>" account dropdown (MET2-2866).
         * Both wait on userFetchPending so neither flashes before /me resolves.
         * Context: docs/components/hero-brand-slot.md (history, geometry, a11y). */}
        {!userFetchPending && !user && (
          <div className="Hero-header Hero-header-brand">
            <img
              src={SVGImage.MoodLogo}
              alt=""
              aria-hidden="true"
              className="mood-logo"
            />
          </div>
        )}
        {!userFetchPending && user && (
          <div className="Hero-header">
            <SubscribersOptions />
          </div>
        )}
        <Slider
          {...settings}
          className="Hero-slider"
          onInit={this.syncSlickInert}
          onReInit={this.syncSlickInert}
          afterChange={this.onSlickAfterChange}
        >
          {slides.map((slide, index) => {
            return (
              <Slide
                key={index}
                index={index}
                isActive={this.state.slideIndex === index}
                // MET2-2953: interactivity + aria-hidden follow the slide the
                // user can SEE (infoIndex, flips at the halfway point), not the
                // one the scroll position has reached (slideIndex, flips at the
                // boundary). heroManaged keeps Slide's className constant so
                // React cannot rewrite over Hero's imperative class writes.
                // Context: docs/components/hero-slide-ownership.md
                isInfoActive={this.state.infoIndex === index}
                heroManaged
                zIndex={(this.state.slidesCount - index) * 10}
                {...slide}
              />
            );
          })}
        </Slider>
        {/* MET2-2855: dots are role="tab" buttons, parent ul is role="tablist" */}
        <ul className={`Hero-navigation-dots ${user ? "user-logged-in" : ""}`} role="tablist" aria-label="Hero slides">
          {slides.map((slide, index) => {
            const isActive = this.state.slideIndex === index;
            return (
              <li key={index} role="none">
                <button
                  type="button"
                  data-index={index}
                  className={`hero-dot${isActive ? " active" : ""}`}
                  onClick={() => this.onClick(index)}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`Slide ${index + 1}${slide.header ? `: ${slide.header}` : ""}`}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}

export default compose(withRouter, withStore)(Hero);
